import {
  Clock,
  Shield,
  Hexagon,
  Activity,
  GitBranch,
  Lock,
} from 'lucide-react'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const STATUS_METRICS = [
  { id: 'system', label: 'System', status: 'operational', icon: Activity },
  { id: 'canon', label: 'Canon', status: 'stable', icon: Shield },
  { id: 'evidence', label: 'Evidence', status: 'blocked', icon: Lock },
  { id: 'sync', label: 'Sync', status: 'pending', icon: Clock },
]

const STATUS_STYLES: Record<string, { color: string; bg: string; text: string }> = {
  operational: { color: 'text-ark-success', bg: 'bg-ark-success', text: 'Operational' },
  stable: { color: 'text-ark-info', bg: 'bg-ark-info', text: 'Stable' },
  blocked: { color: 'text-ark-danger', bg: 'bg-ark-danger', text: 'Blocked' },
  pending: { color: 'text-ark-warning', bg: 'bg-ark-warning', text: 'Pending' },
}

export function ProofStatusBar() {
  return (
    <footer className="h-[2.25rem] bg-ark-deep/90 backdrop-blur-sm border-t border-ark-panel/50 flex items-center px-4 gap-4 shrink-0 z-ark-statusBar">
      {/* Left: System Status */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Hexagon className="w-3.5 h-3.5 text-ark-gold/60" />
          <span className="text-[0.65rem] font-mono text-ark-text-muted uppercase tracking-wider">
            Ark v1.0.0
          </span>
        </div>
        <div className="w-px h-3 bg-ark-panel/50" />
        {STATUS_METRICS.map((metric) => {
          const style = STATUS_STYLES[metric.status]
          return (
            <div key={metric.id} className="flex items-center gap-1.5">
              <div className={cn('w-1.5 h-1.5 rounded-full', style.bg)} />
              <span className={cn('text-[0.65rem] font-mono', style.color)}>
                {metric.label}
              </span>
            </div>
          )
        })}
      </div>

      {/* Center: Metrics */}
      <div className="flex-1 flex items-center justify-center gap-4">
        <div className="flex items-center gap-1.5">
          <GitBranch className="w-3 h-3 text-ark-text-muted" />
          <span className="text-[0.65rem] font-mono text-ark-text-muted">433 Public</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Shield className="w-3 h-3 text-ark-text-muted" />
          <span className="text-[0.65rem] font-mono text-ark-text-muted">372 Core</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Lock className="w-3 h-3 text-ark-text-muted" />
          <span className="text-[0.65rem] font-mono text-ark-text-muted">27 Evidence</span>
        </div>
      </div>

      {/* Right: Time & Mode */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-ark-text-muted" />
          <span className="text-[0.65rem] font-mono text-ark-text-muted">
            {new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div className="w-px h-3 bg-ark-panel/50" />
        <div className="flex items-center gap-1.5">
          <span className="text-[0.65rem] font-mono text-ark-gold uppercase tracking-wider">Locked</span>
          <Lock className="w-3 h-3 text-ark-gold/60" />
        </div>
      </div>
    </footer>
  )
}
